import { useCallback, useEffect, useState } from 'react';

import { useAuth } from '@/hooks/useAuth';
import { ensureUserProfileCallable } from '@/lib/auth';
import { log } from '@/lib/logger';

// Grace period on the scaffolding spinner before we treat it as failed and offer
// a retry. Well above the ~1-2s the snapshot listeners normally take after a
// successful signup, so a slow-but-succeeding signup isn't interrupted.
const SCAFFOLD_GRACE_MS = 15_000;

/**
 * Backs the shared scaffolding-failure UX (AccountSetupProblem). While `waiting`
 * (the account/membership isn't visible yet), a grace timer runs; if it elapses,
 * `failed` flips so the caller shows the error + retry instead of spinning
 * forever. `retry` re-runs the idempotent `ensure_user_profile` recovery, re-arms
 * the timer, and surfaces the error immediately if the call throws (e.g. App
 * Check). `waiting` going false (the listener delivered) clears everything.
 *
 * Purely additive to the qm_web signup flow — it never blocks or alters the happy
 * path, only adds a bounded escape hatch to the loading state.
 */
export function useAccountSetupFallback(waiting: boolean) {
  const auth = useAuth();
  const [failed, setFailed] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [armNonce, setArmNonce] = useState(0);

  useEffect(() => {
    if (!waiting) {
      setFailed(false);
      return;
    }
    const t = setTimeout(() => setFailed(true), SCAFFOLD_GRACE_MS);
    return () => clearTimeout(t);
  }, [waiting, armNonce]);

  const retry = useCallback(() => {
    if (auth.state !== 'signedIn') return;
    setRetrying(true);
    setFailed(false); // back to the spinner while we retry
    setArmNonce((n) => n + 1); // re-arm the grace timer
    log.info('account setup: manual recovery retry', {}, { sendToCloud: true });
    ensureUserProfileCallable({
      displayName: auth.user.displayName ?? undefined,
      locale: navigator.language || 'en-GB',
    })
      .catch((e: unknown) => {
        log.error('account setup: recovery retry failed', { error: String(e) });
        setFailed(true); // fail fast on a thrown error (don't wait out the timer)
      })
      .finally(() => setRetrying(false));
  }, [auth]);

  return { failed, retrying, retry };
}
